import {
  type BaseElement,
  type BasisTheoryElements,
  type CardChangeEvent,
  type CardElementStyle,
  type CardElementEvents,
  type CardExpirationDateElementEvents,
  type CardNumberElementEvents,
  type CardVerificationCodeElementEvents,
  type ChangeEvent,
  type ICardExpirationDateElement,
  type ICardNumberElement,
  type ICardVerificationCodeElement,
  MAESTRO,
  MASTERCARD,
  type Subscription,
  VISA,
} from '@basis-theory/web-elements';

import type {
  CardCollectionConfirmationData,
  CardCollectionFieldChange,
  CardCollectionFieldOptions,
  CardCollectionProvider,
  CardCollectionProviderContext,
  CardCollectionProviderFactory,
  CardCollectionProviderField,
  RenderableCardCollectionBootstrap,
} from './card-collection-provider';
import type { PaypercutPaymentMethodFutureUsage } from '../types/elements';
import { installBasisTheoryCardAutofill } from './basis-theory-card-autofill';
import { createBasisTheoryCardInteractiveReadyGate } from './basis-theory-card-interactive-ready';
import { initializeBasisTheoryV2 } from './basis-theory-v2-initialization';
import {
  tokenizeBasisTheoryWithIdempotentRetry,
  validateBasisTheoryTokenizationResult,
} from './basis-theory-tokenization';

type BasisTheoryCardField =
  | ICardNumberElement
  | ICardExpirationDateElement
  | ICardVerificationCodeElement;

const ACCEPTED_CARD_TYPES = [VISA, MASTERCARD, MAESTRO] as const;
const ACCEPTED_CARD_BRANDS = new Set(ACCEPTED_CARD_TYPES.map(({ type }) => type));
const FIRST_CALENDAR_MONTH = 1;
const LAST_CALENDAR_MONTH = 12;
const EARLIEST_SUPPORTED_EXPIRY_YEAR = 2_000;
const LATEST_SUPPORTED_EXPIRY_YEAR = 9_999;
const MAX_FIELD_MOUNT_ATTEMPTS = 2;
export const BASIS_THEORY_FIELD_MOUNT_ATTEMPT_TIMEOUT_MS = 8_000;

const fieldMountCancelledError = (): Error =>
  new Error('The secure card field mount was cancelled.');

const mountFieldUntilAborted = async (
  field: BasisTheoryCardField,
  options: CardCollectionFieldOptions,
): Promise<void> => {
  if (options.signal.aborted) throw fieldMountCancelledError();

  let rejectAbort: ((error: Error) => void) | undefined;
  let timeoutId: ReturnType<typeof globalThis.setTimeout> | undefined;
  const aborted = new Promise<never>((_resolve, reject) => {
    rejectAbort = reject;
  });
  const handleAbort = () => rejectAbort?.(fieldMountCancelledError());
  options.signal.addEventListener('abort', handleAbort, { once: true });
  // Defer the provider call so even a synchronous Basis Theory exception is
  // handled by the race/finally below and cannot strand the abort listener.
  const mounting = Promise.resolve().then(() => field.mount(options.target));
  const timedOut = new Promise<never>((_resolve, reject) => {
    timeoutId = globalThis.setTimeout(
      () => reject(new Error('Basis Theory secure card field mount timed out.')),
      BASIS_THEORY_FIELD_MOUNT_ATTEMPT_TIMEOUT_MS,
    );
  });
  // If teardown wins the race, retain a rejection handler for a provider
  // promise that may settle after its iframe has already been unmounted.
  void mounting.catch(() => undefined);
  try {
    await Promise.race([mounting, aborted, timedOut]);
    if (options.signal.aborted) throw fieldMountCancelledError();
  } finally {
    options.signal.removeEventListener('abort', handleAbort);
    if (timeoutId !== undefined) globalThis.clearTimeout(timeoutId);
  }
};

const normalizeErrorType = (event: ChangeEvent): 'incomplete' | 'invalid' | undefined => {
  if (event.valid === true) return undefined;
  const type = event.errors?.[0]?.type;
  return type === 'incomplete' || type === 'invalid' ? type : undefined;
};

const normalizeBrand = (value: unknown): string | undefined =>
  typeof value === 'string' && ACCEPTED_CARD_BRANDS.has(value) ? value : undefined;

const normalizeChange = (
  type: CardCollectionFieldOptions['type'],
  event: ChangeEvent & Partial<Pick<CardChangeEvent, 'cardBrand'>>,
): CardCollectionFieldChange => {
  const errorType = normalizeErrorType(event);
  const brand = type === 'cardNumber' ? normalizeBrand(event.cardBrand) : undefined;
  return {
    complete: event.complete && event.valid === true,
    empty: event.empty,
    ...(brand ? { brand } : {}),
    ...(errorType ? { errorType } : {}),
  };
};

const subscribe = (
  field: BasisTheoryCardField,
  options: CardCollectionFieldOptions,
): Subscription[] => {
  const subscriptions: Subscription[] = [];
  const listen = <EventName extends 'ready' | 'change' | 'focus' | 'blur'>(
    eventName: EventName,
    handler: (event: unknown) => void,
  ) => {
    // The three supported card fields share these four event names. Keeping the
    // provider cast at this boundary prevents provider event types from leaking
    // through the rest of the SDK.
    subscriptions.push(
      (field as BaseElement<never, CardElementEvents>).on(eventName, handler as never),
    );
  };

  listen('ready', () => options.events.onReady());
  listen('change', (event) => {
    options.events.onChange(
      normalizeChange(
        options.type,
        event as ChangeEvent & Partial<Pick<CardChangeEvent, 'cardBrand'>>,
      ),
    );
  });
  listen('focus', () => options.events.onFocus());
  listen('blur', () => options.events.onBlur());
  return subscriptions;
};

const createProviderField = async (
  field: BasisTheoryCardField,
  options: CardCollectionFieldOptions,
  onDestroy: () => void,
): Promise<CardCollectionProviderField> => {
  const interactiveReady = createBasisTheoryCardInteractiveReadyGate(
    options.target,
    options.events.onReady,
  );
  const stopAutofillDelegation = installBasisTheoryCardAutofill(
    options.target,
    interactiveReady.observeFrame,
  );
  const guardedOptions: CardCollectionFieldOptions = {
    ...options,
    events: {
      ...options.events,
      onReady: interactiveReady.providerReady,
    },
  };
  let subscriptions: Subscription[] = [];
  try {
    subscriptions = subscribe(field, guardedOptions);
    await mountFieldUntilAborted(field, options);
  } catch (error) {
    interactiveReady.stop();
    stopAutofillDelegation();
    subscriptions.forEach((subscription) => subscription.unsubscribe());
    // A rejected provider mount can still leave a partially attached iframe.
    // Preserve the original error while making a best effort to remove it.
    try {
      field.unmount();
    } catch {
      // Cleanup must not replace the actionable mount failure.
    }
    throw error;
  }

  let destroyed = false;
  return {
    clear: () => {
      if (!destroyed) field.clear();
    },
    focus: () => {
      if (!destroyed) field.focus();
    },
    blur: () => {
      if (!destroyed) field.blur();
    },
    update: async (update) => {
      if (destroyed) return;
      // Basis Theory narrows `value` per concrete field while Paypercut keeps a
      // single provider-neutral interface. The runtime supplies the matching
      // value shape for each registered field type.
      await field.update({
        ...update,
        ...(update.style ? { style: update.style as CardElementStyle } : {}),
      } as never);
    },
    destroy: () => {
      if (destroyed) return;
      destroyed = true;
      onDestroy();
      interactiveReady.stop();
      stopAutofillDelegation();
      subscriptions.forEach((subscription) => subscription.unsubscribe());
      try {
        field.unmount();
      } catch {
        // Provider teardown is best-effort. Local ownership has already been
        // released and must not be stranded by an iframe unmount exception.
      }
    },
  };
};

type BasisTheoryTokenResult = {
  id?: unknown;
  fingerprint?: unknown;
  card?: {
    brand?: unknown;
    last4?: unknown;
    authentication?: unknown;
    expirationMonth?: unknown;
    expirationYear?: unknown;
    expiration_month?: unknown;
    expiration_year?: unknown;
    bin?: unknown;
    funding?: unknown;
    issuer?: { name?: unknown; country?: unknown };
    issuerCountry?: { alpha2?: unknown };
    issuer_country?: { alpha2?: unknown } | unknown;
    segment?: unknown;
    additional?: ReadonlyArray<{
      brand?: unknown;
      funding?: unknown;
      issuer?: { name?: unknown; country?: unknown };
      issuer_country?: { alpha2?: unknown } | unknown;
    } | null>;
  };
};

const normalizedString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim();
  return normalized || undefined;
};

const normalizedInteger = (value: unknown, minimum: number, maximum: number): number | undefined =>
  Number.isSafeInteger(value) && Number(value) >= minimum && Number(value) <= maximum
    ? Number(value)
    : undefined;

const DOMESTIC_CARD_NETWORKS = new Set(['bancontact', 'cartes_bancaires', 'dankort']);
const CARD_NETWORK_ALIASES: Record<string, string> = {
  amex: 'american_express',
  americanexpress: 'american_express',
  cartesbancaires: 'cartes_bancaires',
  dinersclub: 'diners_club',
  master_card: 'mastercard',
  unionpay: 'union_pay',
};

const normalizeCardNetwork = (value: unknown): string | undefined => {
  const normalized = normalizedString(value)
    ?.toLowerCase()
    .replace(/[\s-]+/g, '_');
  return normalized ? (CARD_NETWORK_ALIASES[normalized] ?? normalized) : undefined;
};

const normalizeCardMetadataValue = (value: unknown): string | undefined =>
  normalizedString(value)?.toLowerCase();

const issuerCountryValue = (value: unknown): unknown =>
  value && typeof value === 'object' ? (value as { alpha2?: unknown }).alpha2 : value;

const resolveCardBrands = (card: BasisTheoryTokenResult['card']) => {
  const primary = normalizeCardNetwork(card?.brand);
  const additional = Array.from(
    new Set(
      (Array.isArray(card?.additional) ? card.additional : [])
        .map((entry) => normalizeCardNetwork(entry?.brand))
        .filter((brand): brand is string => Boolean(brand)),
    ),
  );
  const primaryIsDomestic = primary ? DOMESTIC_CARD_NETWORKS.has(primary) : false;
  const brand =
    primary && !primaryIsDomestic
      ? primary
      : additional.find((candidate) => !DOMESTIC_CARD_NETWORKS.has(candidate));
  const displayBrand = [primary, ...additional].find(
    (candidate) => candidate && DOMESTIC_CARD_NETWORKS.has(candidate),
  );
  return {
    brand,
    displayBrand: displayBrand ?? additional.find((candidate) => candidate !== brand),
  };
};

const mapConfirmationData = (
  result: BasisTheoryTokenResult,
  tokenType: 'token' | 'token_intent',
  cardholderName?: string,
): CardCollectionConfirmationData => {
  const tokenId = normalizedString(result.id);
  if (!tokenId) throw new Error('Basis Theory returned no token identifier.');
  const card = result.card;
  const additionalData = (Array.isArray(card?.additional) ? card.additional : []).filter(
    (entry): entry is NonNullable<typeof entry> => Boolean(entry && typeof entry === 'object'),
  );
  const firstAdditionalValue = <T>(
    select: (entry: NonNullable<(typeof additionalData)[number]>) => T | undefined,
  ) => additionalData.map(select).find((value): value is T => value !== undefined);
  const last4 = normalizedString(card?.last4);
  const issuerCountry = card?.issuerCountry?.alpha2;
  const legacyIssuerCountry =
    card?.issuer_country && typeof card.issuer_country === 'object'
      ? (card.issuer_country as { alpha2?: unknown }).alpha2
      : card?.issuer_country;
  const country = normalizedString(
    issuerCountry ??
      legacyIssuerCountry ??
      card?.issuer?.country ??
      firstAdditionalValue((entry) =>
        issuerCountryValue(entry.issuer_country ?? entry.issuer?.country),
      ),
  );
  const funding =
    normalizeCardMetadataValue(card?.funding) ??
    firstAdditionalValue((entry) => normalizeCardMetadataValue(entry.funding));
  const issuer =
    normalizedString(card?.issuer?.name) ??
    firstAdditionalValue((entry) => normalizedString(entry.issuer?.name));
  const expMonth = normalizedInteger(
    card?.expirationMonth ?? card?.expiration_month,
    FIRST_CALENDAR_MONTH,
    LAST_CALENDAR_MONTH,
  );
  const expYear = normalizedInteger(
    card?.expirationYear ?? card?.expiration_year,
    EARLIEST_SUPPORTED_EXPIRY_YEAR,
    LATEST_SUPPORTED_EXPIRY_YEAR,
  );
  const { brand, displayBrand } = resolveCardBrands(card);
  const safeCard = {
    ...(brand ? { brand } : {}),
    ...(displayBrand ? { display_brand: displayBrand } : {}),
    ...(last4 && /^\d{4}$/.test(last4) ? { last4 } : {}),
    ...(expMonth === undefined ? {} : { exp_month: expMonth }),
    ...(expYear === undefined ? {} : { exp_year: expYear }),
    ...(normalizedString(result.fingerprint)
      ? { fingerprint: normalizedString(result.fingerprint) }
      : {}),
    ...(normalizedString(card?.bin) ? { iin: normalizedString(card?.bin) } : {}),
    ...(funding ? { funding } : {}),
    ...(issuer ? { issuer } : {}),
    ...(country ? { country } : {}),
    ...(normalizedString(card?.segment)?.toLowerCase()
      ? { segment: normalizedString(card?.segment)?.toLowerCase() }
      : {}),
    ...(cardholderName ? { cardholder_name: cardholderName } : {}),
  };
  return {
    instrumentToken: { type: tokenType, id: tokenId },
    ...(card?.authentication === 'sca_required' || card?.authentication === 'sca_not_required'
      ? { authentication: card.authentication }
      : {}),
    card: safeCard,
  };
};

const createField = (
  provider: BasisTheoryElements,
  options: CardCollectionFieldOptions,
): BasisTheoryCardField => {
  if (!provider) throw new Error('Basis Theory Elements did not initialize.');

  const common = {
    targetId: options.target.id,
    inputMode: 'numeric' as const,
    placeholder: options.placeholder,
    style: options.style as CardElementStyle,
    validateOnChange: true,
    'aria-label': options.ariaLabel,
  };

  switch (options.type) {
    case 'cardNumber':
      return provider.createElement('cardNumber', {
        ...common,
        autoComplete: 'cc-number',
        cardTypes: [...ACCEPTED_CARD_TYPES],
        // Paypercut owns accepted, detected, and invalid brand presentation so
        // it remains consistent across the combined and split Elements APIs.
        iconPosition: 'none',
      });
    case 'cardExpiry':
      return provider.createElement('cardExpirationDate', {
        ...common,
        autoComplete: 'cc-exp',
      });
    case 'cardCvc':
      return provider.createElement('cardVerificationCode', {
        ...common,
        autoComplete: 'cc-csc',
      });
  }
};

export const createBasisTheoryV2CardProvider: CardCollectionProviderFactory = async (
  bootstrap: RenderableCardCollectionBootstrap,
  context: CardCollectionProviderContext,
): Promise<CardCollectionProvider> => {
  if (bootstrap.driver !== 'basis_theory_v2') {
    throw new Error('The configured card provider is not supported.');
  }

  const provider = await initializeBasisTheoryV2(
    {
      publicKey: bootstrap.publicKey,
      options: {
        ...(!bootstrap.livemode ? { environment: 'test' } : {}),
        useNetworkCheck: true,
      },
    },
    context.signal,
  );

  const fields = new Map<CardCollectionFieldOptions['type'], BasisTheoryCardField>();

  const mountField = async (
    options: CardCollectionFieldOptions,
  ): Promise<CardCollectionProviderField> => {
    const field = createField(provider, options);
    fields.set(options.type, field);
    try {
      return await createProviderField(field, options, () => {
        if (fields.get(options.type) === field) fields.delete(options.type);
      });
    } catch (error) {
      if (fields.get(options.type) === field) fields.delete(options.type);
      throw error;
    }
  };

  return {
    createField: async (options) => {
      let lastError: unknown;
      for (let attempt = 0; attempt < MAX_FIELD_MOUNT_ATTEMPTS; attempt += 1) {
        if (options.signal.aborted) {
          throw lastError ?? fieldMountCancelledError();
        }
        try {
          return await mountField(options);
        } catch (error) {
          lastError = error;
          if (options.signal.aborted || attempt + 1 === MAX_FIELD_MOUNT_ATTEMPTS) throw error;
        }
      }
      throw lastError;
    },
    createConfirmationData: async (
      requestId,
      cardholderName,
      futureUsage?: PaypercutPaymentMethodFutureUsage,
    ) => {
      const number = fields.get('cardNumber') as ICardNumberElement | undefined;
      const expiry = fields.get('cardExpiry') as ICardExpirationDateElement | undefined;
      const cvc = fields.get('cardCvc') as ICardVerificationCodeElement | undefined;
      if (!number || !expiry || !cvc) throw new Error('The secure card fields are not ready.');
      const tokenData = {
        number,
        expiration_month: expiry.month(),
        expiration_year: expiry.year(),
        cvc,
        ...(cardholderName ? { cardholderName } : {}),
      };
      const tokenType = futureUsage ? 'token' : 'token_intent';
      const request = { type: 'card' as const, data: tokenData };
      const requestOptions = {
        idempotencyKey: `paypercut-elements:${requestId}:card-tokenization`,
      };
      const createToken = async (): Promise<BasisTheoryTokenResult> => {
        const result = futureUsage
          ? await provider.tokens.create(request, requestOptions)
          : await provider.tokenIntents.create(request, requestOptions);
        return validateBasisTheoryTokenizationResult<BasisTheoryTokenResult>(result);
      };
      const result = await tokenizeBasisTheoryWithIdempotentRetry(createToken);
      return mapConfirmationData(result, tokenType, cardholderName);
    },
  };
};

// These imports intentionally remain type-checked against all three v2 event
// unions. They catch an incompatible provider upgrade at compile time.
type SupportedBasisTheoryEvents =
  | CardNumberElementEvents
  | CardExpirationDateElementEvents
  | CardVerificationCodeElementEvents;
void (undefined as SupportedBasisTheoryEvents | undefined);
